/* eslint-disable no-console */
import React, { useContext, useState, useCallback } from 'react';

import { ICustomer, ICustomerFull } from '../types/ICustomer';
import { useAgent } from '../hooks/useAgent';
import { useEditFavorite } from '../hooks/useEditFavorite';

type IProfAgentContextState = {
    agentData: Record<string, ICustomerFull>;
    customers: Record<string, ICustomerFull>;
    handleAddCustomer: (offerId: string, customer: ICustomer) => Promise<void>;
    handleOnChangeBusinessLike: (offerId: string, customerId: string, isLike: boolean) => Promise<void>;
    handleAddCustomerToList: (customer: ICustomer) => void;
}

const ProfAgentContext = React.createContext<IProfAgentContextState | undefined>(undefined);

export const AgentFavoritesProvider: React.FC = ({ children }) => {
    const { onEditFavorite } = useEditFavorite();
    const { agentData, clearCache } = useAgent();

    const [ customersTmp, setCustomersTmp ] = useState<Record<string, ICustomerFull> | null>(null);
    const setCustomers = React.useCallback((
        cb: (prev: typeof customersTmp) => NonNullable<typeof customersTmp>
    ) => {
        setCustomersTmp(cb);
        clearCache();
    }, [ clearCache ]);

    const customers = customersTmp ?? {};

    React.useEffect(() => {
        if (agentData) setCustomersTmp(agentData);
    }, [ agentData ]);

    const handleAddCustomer = useCallback(async (offerId: string, customer: ICustomer) => {
        try {
            await onEditFavorite({
                customerId: customer.id,
                offerId,
                isFavorite: true
            });
            setCustomers(prev => ({
                ...prev,
                [customer.id]: {
                    ...customer,
                    favoritesCount: 1,
                    favoritesList: [ offerId ],
                    searchesCount: 0,
                    searchesList: []
                }
            }));
        } catch (e: unknown) {
            console.error('onEditFavorite error', e);
        }
    }, [ onEditFavorite, setCustomers ]);

    const handleAddCustomerToList = useCallback((customer: ICustomer) => {
        setCustomers(prev => ({
            ...prev,
            [customer.id]: {
                ...customer,
                favoritesCount: 1,
                favoritesList: [ ],
                searchesCount: 0,
                searchesList: []
            }
        }));
    }, [ setCustomers ]);

    const handleOnChangeBusinessLike = useCallback(async (offerId: string, customerId: string, isLike: boolean) => {
        try {
            await onEditFavorite({
                customerId,
                offerId,
                isFavorite: ! isLike
            });
            setCustomers(prevState => {
                const ps = prevState ?? {};
                const customerPrev = ps[customerId];
                const hasOffer = customerPrev?.favoritesList.includes(offerId);
                const favoritesList = hasOffer ?
                    customerPrev?.favoritesList.filter(id => id !== offerId) :
                    [ ...customerPrev?.favoritesList ?? [], offerId ];

                return ({
                    ...ps,
                    [customerId]: {
                        ...customerPrev ?? {},
                        favoritesCount: favoritesList.length,
                        favoritesList,
                    }
                });
            });
        } catch (e: unknown) {
            // eslint-disable-next-line no-console
            console.error('onEditFavorite error', e);
        }
    }, [ onEditFavorite, setCustomers ]);

    const values: IProfAgentContextState = {
        agentData: agentData ?? {},
        customers,
        handleAddCustomer,
        handleOnChangeBusinessLike,
        handleAddCustomerToList,
    };

    return <ProfAgentContext.Provider value={values}>{children}</ProfAgentContext.Provider>;
};

export const useProfAgentContext = (): IProfAgentContextState => {
    const context = useContext(ProfAgentContext);

    if (context === undefined) {
        throw new Error('useProfAgentContext must be used within a ProfAgentContext.Provider');
    }

    return context;
};
